import moment from "moment";
import placeholderGem from "../../assets/placeholder_gem.png";
import leaderboardBackground from "../../assets/leaderboard.png";
import leaderboardClose from "../../assets/leaderboard_close.png";
import leaderboardCup from "../../assets/leaderboard_cup.png";
import leaderboardStar1 from "../../assets/leaderboard_star1.png";
import leaderboardStar2 from "../../assets/leaderboard_star2.png";
import AlertModal from "../AlertModal";
import "../styles.css";

type LeaderboardHouse = {
  houseId: string;
  name: string;
  image: string;
  points: number;
  ranking?: number;
  isWinner?: boolean;
};

type LeaderboardCompetition = {
  startDate?: string;
  endDate?: string;
  winnerHouseId?: string;
};

type LeaderboardEntry = {
  competition?: LeaderboardCompetition;
  houses?: LeaderboardHouse[];
};

type LeaderboardModalProps = {
  visible: boolean;
  loading: boolean;
  data: LeaderboardEntry | null;
  onClose: () => void;
};

const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  visible,
  loading,
  data,
  onClose,
}) => {
  const hasWinningHouse = Boolean(data?.competition?.winnerHouseId);
  const houses = [...(data?.houses || [])].sort((a, b) => {
    if (hasWinningHouse) {
      const rankA = Number(a?.points ?? Number.MAX_SAFE_INTEGER);
      const rankB = Number(b?.points ?? Number.MAX_SAFE_INTEGER);
      if (rankA !== rankB) return rankB - rankA;
    }

    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });

  const startDate = data?.competition?.startDate;
  const endDate = data?.competition?.endDate;
  const startMoment = startDate ? moment(startDate) : null;
  const endMoment = endDate ? moment(endDate) : null;
  const today = moment().startOf("day");

  const totalDays =
    startMoment && endMoment ? endMoment.diff(startMoment, "days") + 1 : 0;
  const elapsedDays =
    startMoment && endMoment
      ? Math.min(Math.max(today.diff(startMoment, "days") + 1, 0), totalDays)
      : 0;
  const daysLeft = endMoment ? Math.max(endMoment.diff(today, "days") + 1, 0) : 0;
  const timeProgress = totalDays > 0 ? Math.min(Math.max(elapsedDays / totalDays, 0), 1) : 0;
  const competitionProgress = timeProgress * 100;
  const leaderPoints = houses.reduce((maxPoints, house) => {
    const points = Number(house?.points || 0);
    return Math.max(maxPoints, points);
  }, 0);

  const titleMonth = startMoment?.isValid()
    ? startMoment.format("MMMM")
    : moment().format("MMMM");
  const winnerHouseName =
    houses.find((house) => house.houseId === data?.competition?.winnerHouseId)?.name ||
    "Winning House";

  if (!visible) return null;

  const noDataResponse = !loading && houses.length === 0;
  if (noDataResponse) {
    return (
      <AlertModal
        visible={true}
        title="Check Back Soon!"
        message="House points will be tracked here."
        onConfirm={onClose}
      />
    );
  }

  const formatRank = (rank: number) => {
    const remainder100 = rank % 100;
    if (remainder100 >= 11 && remainder100 <= 13) return `${rank}th`;

    const remainder10 = rank % 10;
    if (remainder10 === 1) return `${rank}st`;
    if (remainder10 === 2) return `${rank}nd`;
    if (remainder10 === 3) return `${rank}rd`;
    return `${rank}th`;
  };

  return (
    <div className="ModalOverlay z-50">
      <div className="modal-mood-content flex flex-1">
        <div className="ModalBody relative flex flex-1 flex-col" style={{paddingTop: '0px'}}>
           
        <div className="pb-0 relative" >
          <button
            onClick={onClose}
            className="absolute top-2 right-3 z-10 leading-none text-white"
            aria-label="Close leaderboard"
          >
           <img className="h-5 w-5" alt="Close" src={leaderboardClose}/>
          </button>
          <div><img src={leaderboardBackground} alt="" style={{width: '100%'}}/></div>

          <div className="relative z-10 -mt-28 rounded-b-lg px-5 pb-4 ">
            <div className="flex items-end justify-center  leading-none">
              <img className="object-contain" alt="" src={leaderboardStar2} style={{marginRight: '-12px'}}/>
              <img className="object-contain" alt="" src={leaderboardCup}/>
              <img className="object-contain" alt="" src={leaderboardStar1} style={{marginLeft: '-12px'}}/>
            </div>
            
            {!loading && houses.length > 0 &&
              (hasWinningHouse ? (
                <div className="text-center inter-24-bold mt-4">{titleMonth} Champion</div>
              ) : (
                <div className="text-center inter-24-bold mt-4">{titleMonth} House Points</div>
              ))}
            
            {!loading && houses.length > 0 &&
              (hasWinningHouse ? (
              <div className="inter-text-14 justify-center flex">
                Congrats, {winnerHouseName}!
              </div>
            ) : (
            <div className="mt-4">
              <div className="w-full rounded-full border h-7 overflow-hidden relative" style={{ borderColor: "#F875DA",  }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(Math.max(competitionProgress, 0), 100)}%`,
                    backgroundColor: "#FFBAF1",
                  }}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 inter-text-14-semibold">
                {daysLeft === 0 ? "Last Day!" : `${daysLeft} Days Left`}
                </span>
              </div>
              <div className="flex justify-between inter-regular-14 mt-2 px-1">
                <span>{startMoment?.isValid() ? startMoment.format("MMM D") : "-"}</span>
                <span>{endMoment?.isValid() ? endMoment.format("MMM D") : "-"}</span>
              </div>
            </div>
            ))}
            

            <div className="mt-4 space-y-2 max-h-[275px] overflow-y-auto">
              {loading && (
                <div className="text-center inter-text-14-semibold py-6">Loading leaderboard...</div>
              )}

              {!loading && houses.length === 0 && null}

              {!loading &&
                houses.map((house, index) => {
                  const housePoints = Number(house?.points || 0);
                  const normalizedProgress =
                    leaderPoints > 0 ? (housePoints * timeProgress) / leaderPoints : 0;
                  const houseProgress = Math.min(Math.max(normalizedProgress, 0), 1);
                  const displayRank = Number(house?.ranking ?? index + 1);

                  return (
                    <div
                      key={house.houseId}
                      className="relative border rounded-xl p-2 overflow-hidden"
                      style={{
                        borderColor: "#EA088C",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <div
                        className="absolute left-0 top-0 h-full rounded-r-xl"
                        style={{
                          width: `${houseProgress * 100}%`,
                          // backgroundColor: house.isWinner ? "#F2BFD9" : "#F3CDA1",
                          background: "linear-gradient(90deg, #EB84AC 0%, #FFD5A5 100%)",
                          transition: "width 700ms ease",
                        }}
                      />

                      <div className="relative z-10 flex items-center gap-2">
                        <div className="rounded-full p-0.5 flex items-center justify-center">
                          <img
                            src={house.image || placeholderGem}
                            alt={house.name}
                            className="w-9 h-9 rounded-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src = placeholderGem;
                            }}
                          />
                        </div>

                        <div className="flex-1 text-left leading-tight">
                          <div className="inter-text-14-semibold flex items-center gap-2" style={{ textAlign: "left" }}>
                            <span>{house.name}</span>
                            {house.isWinner && (
                              <span className="inter-regular-14" style={{ color: "#EA088C" }}>
                                Winner
                              </span>
                            )}
                          </div>
                          <div className="inter-regular-14">{housePoints} pts</div>
                        </div>

                        {hasWinningHouse && (
                          <div className="inter-text-20-semibold flex items-center gap-1">
                            {displayRank === 1 && (
                              <img src={require("../../assets/crown.png")} alt="Crown" className="w-8 h-8 object-contain" />
                            )}
                            {formatRank(displayRank)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="text-center mt-5 mb-1 leading-tight">
             
              <div className="text-center mt-5 mb-1 leading-tight">
                {hasWinningHouse ? (
                  <div className="inter-text-16 justify-center flex">
                    {winnerHouseName} ruled the leaderboard this month! Stay active, earn points, and your house could be next month’s champion.
                  </div>
                ) : (
                  <>
                    {/* <div className="inter-regular-14">Everyone in the winning house gets</div>
                    <div className="inter-text-14-semibold" style={{ color: "#EA088C" }}>
                      30 BONUS POINTS +
                      <span style={{ color: "var(--black)", fontFamily: "Inter-Regular", marginLeft: 4 }}>
                        every month it resets!
                      </span>
                    </div> */}
                    <div className="inter-regular-14">Each month, everyone in the winning  house gets</div>
                    <div className="inter-text-14-semibold" style={{ color: "#EA088C" }}>
                      30 BONUS POINTS.
                    </div>
                    
                  </>
                )}
              </div>

              
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardModal;
